import { Component, ElementRef, OnInit, ViewChild, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { validateAndFocusFirstError } from '../../utils/form-validation';
import { friendlyError } from '../../utils/friendly-error';
import { CustomerService } from '../../services/customer.service';
import { AuthService } from '../../services/auth.service';
import { CreateClienteRequest, Cliente, UpdateClienteRequest } from '../../models/sala.model';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notification.service';
import { ActionCooldownService } from '../../services/action-cooldown.service';
import { PaginationComponent } from '../ui/pagination.component';
import { UiSkeletonComponent } from '../ui/ui-skeleton.component';
import { ManagementRecordCardComponent } from '../ui/management-record-card.component';
import { finishLoadingAfter } from '../../utils/loading';
import { UiStateComponent } from '../ui/ui-state.component';
import { CooldownBubbleDirective } from '../../directives/cooldown-bubble.directive';

@Component({
  selector: 'app-usuarios',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, PaginationComponent, UiSkeletonComponent, ManagementRecordCardComponent, UiStateComponent, CooldownBubbleDirective],
  template: `
    <div class="usuarios-container paginated-module">
      <div class="usuarios-header">
        <div class="search-group">
          <label class="customer-search" aria-label="Buscar clientes">
            <i class="bi bi-search" aria-hidden="true"></i>
            <input type="search" [value]="searchDraft()" (input)="onSearchInput($any($event.target).value)" (keyup.enter)="buscarClientes()"
              placeholder="Buscar por nombre o correo" autocomplete="off" />
            <button *ngIf="searchDraft()" type="button" (click)="limpiarBusqueda()" aria-label="Limpiar búsqueda">
              <i class="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          </label>
          <button type="button" class="search-submit" (click)="buscarClientes()" [disabled]="isLoading()" cooldownBubble data-cooldown-key="usuarios-search">
            <i class="bi" [class.bi-search]="!isLoading()" [class.bi-arrow-repeat]="isLoading()" aria-hidden="true"></i>
            <span>{{ isLoading() ? 'Buscando...' : 'Buscar' }}</span>
          </button>
        </div>
        <div class="header-tools">
          <label class="page-size-field" aria-label="Cantidad de clientes por página">
            <span>Registros</span>
            <select [ngModel]="pageSize()" (ngModelChange)="onPageSizeChange($event)" [disabled]="isLoading()">
              <option *ngFor="let size of pageSizeOptions" [ngValue]="size">{{ size }} por página</option>
            </select>
          </label>
          <button (click)="showCreateForm.set(true)" class="btn-primary">
            + Nuevo Cliente
          </button>
        </div>
      </div>

      <!-- Formulario de creación -->
      <div *ngIf="showCreateForm() && !editingCliente()" class="modal-overlay" (click)="closeForm()">
       <div class="modal-content" (click)="$event.stopPropagation()">
        <div class="form-header">
          <h3>Crear Nuevo Cliente</h3>
          <button (click)="closeForm()" class="close-btn">×</button>
        </div>
        <form #clienteFormRef [formGroup]="clienteForm" (ngSubmit)="crearCliente()" class="cliente-form">
          <div class="form-group">
            <label for="name">Nombre Completo *</label>
            <input 
              type="text" 
              id="name" 
              formControlName="name" 
              class="form-control"
              placeholder="Ej: Juan Pérez"
            />
            <small *ngIf="clienteForm.get('name')?.hasError('required') && clienteForm.get('name')?.touched" class="error">
              El nombre es requerido
            </small>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="email">Email *</label>
              <input 
                type="email" 
                id="email" 
                formControlName="email" 
                class="form-control"
                placeholder="juan.perez@example.com"
              />
              <small *ngIf="clienteForm.get('email')?.hasError('required') && clienteForm.get('email')?.touched" class="error">
                El email es requerido
              </small>
              <small *ngIf="clienteForm.get('email')?.hasError('email') && clienteForm.get('email')?.touched" class="error">
                El email no es válido
              </small>
            </div>

            <div class="form-group">
              <label for="phone_number">Teléfono *</label>
              <input 
                type="text" 
                id="phone_number" 
                formControlName="phone_number" 
                class="form-control"
                placeholder="+50312345678"
              />
              <small *ngIf="clienteForm.get('phone_number')?.hasError('required') && clienteForm.get('phone_number')?.touched" class="error">
                El teléfono es requerido
              </small>
            </div>
          </div>

          <div class="form-group notice-box">
            <i class="bi bi-info-circle-fill" aria-hidden="true"></i>
            <p class="hint">
              Al crear el cliente se generará automáticamente un <strong>enlace seguro</strong> que se enviará a su correo para que el usuario establezca su propia contraseña. El enlace expira en <strong>1 hora</strong>. Si expira, el administrador podrá reenviar un nuevo enlace.
            </p>
          </div>

          <div class="form-actions">
            <button type="button" class="btn-cancel" (click)="closeForm()">Cancelar</button>
            <button type="submit" class="btn-save" [disabled]="isCreating()" cooldownBubble data-cooldown-key="usuarios-create">
              {{ isCreating() ? 'Creando...' : 'Crear Cliente' }}
            </button>
          </div>
        </form>
       </div>
      </div>

      <!-- Formulario de edición -->
      <div *ngIf="editingCliente()" class="modal-overlay" (click)="cancelarEdicion()">
       <div class="modal-content" (click)="$event.stopPropagation()">
        <div class="form-header">
          <h3>Editar Cliente</h3>
          <button (click)="cancelarEdicion()" class="close-btn">×</button>
        </div>
        <form #editFormRef [formGroup]="editForm" (ngSubmit)="guardarEdicion()" class="cliente-form">
          <div class="form-group">
            <label for="edit_name">Nombre Completo *</label>
            <input 
              type="text" 
              id="edit_name" 
              formControlName="name" 
              class="form-control"
              placeholder="Ej: Juan Pérez"
            />
            <small *ngIf="editForm.get('name')?.hasError('required') && editForm.get('name')?.touched" class="error">
              El nombre es requerido
            </small>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="edit_email">Email *</label>
              <input 
                type="email" 
                id="edit_email" 
                formControlName="email" 
                class="form-control"
                placeholder="juan.perez@example.com"
              />
              <small *ngIf="editForm.get('email')?.hasError('required') && editForm.get('email')?.touched" class="error">
                El email es requerido
              </small>
              <small *ngIf="editForm.get('email')?.hasError('email') && editForm.get('email')?.touched" class="error">
                El email no es válido
              </small>
            </div>

            <div class="form-group">
              <label for="edit_phone_number">Teléfono *</label>
              <input 
                type="text" 
                id="edit_phone_number" 
                formControlName="phone_number" 
                class="form-control"
                placeholder="+50312345678"
              />
              <small *ngIf="editForm.get('phone_number')?.hasError('required') && editForm.get('phone_number')?.touched" class="error">
                El teléfono es requerido
              </small>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="btn-cancel" (click)="cancelarEdicion()">Cancelar</button>
            <button type="submit" class="btn-save" [disabled]="isUpdating()" cooldownBubble data-cooldown-key="usuarios-edit">
              {{ isUpdating() ? 'Guardando...' : 'Guardar Cambios' }}
            </button>
          </div>
        </form>
       </div>
      </div>

      <!-- Lista de clientes -->
      <div class="clientes-list">
        <app-ui-skeleton
          *ngIf="isLoading()"
          variant="table"
          [count]="6"
          [columnCount]="4"
          label="Cargando clientes"
          [compact]="true"
        ></app-ui-skeleton>

        <app-ui-state
          *ngIf="customerService.clientes().length === 0 && !showCreateForm() && !isLoading()"
          tone="empty"
          [icon]="searchTerm() ? '' : 'bi bi-people'"
          [title]="searchTerm() ? 'No encontramos clientes' : 'Aún no hay clientes'"
          [message]="searchTerm() ? 'No encontramos clientes con ese nombre o correo.' : 'No hay clientes registrados todavía.'"
          [actionLabel]="searchTerm() ? '' : 'Crear primer cliente'"
          (action)="showCreateForm.set(true)"
        ></app-ui-state>

        <div *ngIf="customerService.clientes().length > 0 && !isLoading()" class="clientes-directory">
          <app-management-record-card *ngFor="let cliente of customerService.clientes(); trackBy: trackCliente" [clickable]="false" [tone]="cliente.is_active ? 'default' : 'inactive'" kind="people" [status]="cliente.is_active ? 'success' : 'danger'">
            <span recordIcon><i class="bi bi-person-fill"></i></span>
            <div recordPrimary>
              <small>CLIENTE</small>
              <strong>{{ cliente.name }}</strong>
              <span *ngIf="cliente.created_at">Desde {{ formatDate(cliente.created_at) }}</span>
            </div>
            <div recordSummary>
              <small>CONTACTO</small>
              <strong>{{ cliente.email }}</strong>
              <span>{{ cliente.phone_number || 'Teléfono no registrado' }}</span>
            </div>
            <div recordStatus>
              <small>ESTADO</small>
              <span class="status-badge" [class.active]="cliente.is_active" [class.inactive]="!cliente.is_active">
                {{ cliente.is_active ? 'Activo' : 'Inactivo' }}
              </span>
            </div>
            <div recordActions>
              <button type="button" class="icon-action" (click)="editarCliente(cliente)" title="Editar datos del cliente" aria-label="Editar datos del cliente" [disabled]="processingCustomerId() === cliente.user_id">
                <i class="bi bi-pencil-square" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="icon-action status-toggle-btn"
                [class.is-active]="cliente.is_active"
                [class.is-inactive]="!cliente.is_active"
                (click)="toggleEstadoCliente(cliente)"
                [title]="cliente.is_active ? 'Desactivar' : 'Activar'"
                [attr.aria-label]="cliente.is_active ? 'Desactivar cliente ' + cliente.name : 'Activar cliente ' + cliente.name"
                [disabled]="processingCustomerId() === cliente.user_id"
              >
                <i class="bi" [class.bi-toggle-on]="cliente.is_active" [class.bi-toggle-off]="!cliente.is_active" aria-hidden="true"></i>
              </button>
              <button type="button" class="icon-action" (click)="enviarRecuperacion(cliente)" title="Enviar enlace de recuperación al cliente" aria-label="Enviar enlace de recuperación al cliente" [disabled]="processingCustomerId() === cliente.user_id">
                <i class="bi bi-envelope-arrow-up-fill" aria-hidden="true"></i>
              </button>
              <button type="button" class="icon-action danger" (click)="eliminarCliente(cliente)" title="Enviar cliente a la Papelera" aria-label="Enviar cliente a la Papelera" [disabled]="processingCustomerId() === cliente.user_id">
                <i class="bi bi-trash3" aria-hidden="true"></i>
              </button>
            </div>
          </app-management-record-card>
        </div>
      </div>
      <app-pagination [page]="currentPage()" [pages]="totalPages()" [total]="totalCustomers()" [limit]="pageSize()" [disabled]="isLoading()" itemLabel="clientes" ariaLabel="Paginación de clientes" (pageChange)="cambiarPagina($event)"></app-pagination>
    </div>

  `,
  styles: [`
    .usuarios-container {
      width: 100%;
    }

    .usuarios-header {
      display: grid;
      grid-template-columns: minmax(260px, 1fr) auto;
      align-items: center;
      gap: 12px;
      margin-bottom: 20px;
    }
    .search-group {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }
    .search-group .customer-search {
      flex: 0 1 430px;
    }
    .search-submit {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: .45rem;
      height: 42px;
      padding: 0 1rem;
      border: 1px solid var(--ts-border-blue-150-2);
      border-radius: 10px;
      color: var(--ts-text-blue-550-7);
      background: var(--ts-bg-blue-50-11);
      font-weight: 700;
      font-size: 13px;
      white-space: nowrap;
      cursor: pointer;
      transition: background .2s ease, border-color .2s ease;
    }
    .search-submit:hover:not(:disabled) { background: var(--ts-bg-blue-50-20); border-color: var(--ts-border-blue-550-2); }
    .search-submit:disabled { cursor: wait; opacity: .75; }
    .page-size-field {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: var(--ts-text-gray-550);
      font-size: 13px;
      font-weight: 600;
    }
    .page-size-field select {
      min-width: 145px;
      border: 1px solid var(--ts-border-blue-150-5);
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 13px;
      background: var(--ts-text);
      color: var(--ts-text-indigo-850);
    }

    .usuarios-header h2 {
      margin: 0;
      font-size: 24px;
      font-weight: 700;
      color: var(--ts-text-gray-800);
    }

    .btn-primary {
      background: var(--ts-bg);
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.3s ease;
    }

    .btn-primary:hover {
      background: var(--ts-bg-gray-800);
    }

    .form-container {
      background: white;
      border-radius: 12px;
      padding: 32px;
      box-shadow: 0 2px 8px var(--ts-shadow-gray-1000-a10);
      margin-bottom: 30px;
    }

    .form-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
    }

    .form-header h3 {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 32px;
      color: var(--ts-text-muted);
      cursor: pointer;
      line-height: 1;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
    }

    .close-btn:hover {
      color: var(--ts-text);
    }

    .cliente-form {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
    }

    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 600;
      color: var(--ts-text-muted);
      font-size: 14px;
    }

    .form-control {
      width: 100%;
      padding: 10px 14px;
      color: var(--ts-text-soft-2);
      background: var(--ts-scrollbar-track);
      border: 2px solid var(--ts-divider);
      border-radius: 6px;
      font-size: 14px;
      box-sizing: border-box;
      transition: all 0.3s ease;
    }

    .form-control::placeholder { color: var(--ts-text-sky-450-11); }

    .form-control:focus {
      outline: none;
      border-color: var(--ts-primary);
      box-shadow: 0 0 0 3px var(--ts-shadow-blue-400-a16-2);
    }

    .error {
      display: block;
      color: var(--ts-text-red-200-18) !important;
      font-size: 12px;
      margin-top: 4px;
    }

    .hint {
      display: block;
      color: var(--ts-text-muted);
      font-size: 12px;
      margin-top: 4px;
      font-style: italic;
    }

    /* Aviso de contraseña temporal: fondo oscuro consistente con el resto
       del modal — así el texto en negrita (forzado a var(--ts-text) por las
       reglas globales del tema) queda legible en vez de casi invisible
       sobre un fondo claro. */
    .notice-box {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      margin-top: 12px;
      padding: 12px 14px;
      border: 1px solid var(--ts-border-orange-550-a38);
      border-left: 4px solid var(--ts-border-orange-550-2);
      border-radius: 8px;
      background: var(--ts-bg-orange-550-a12);
    }

    .notice-box i {
      font-size: 1.1rem;
      color: var(--ts-text-amber-450-2);
      line-height: 1.4;
      flex-shrink: 0;
    }

    .notice-box .hint {
      color: var(--ts-text-amber-200-4) !important;
      font-size: 13px;
      line-height: 1.5;
      margin: 0;
      font-style: normal;
      font-weight: 500;
    }

    .notice-box strong {
      color: var(--ts-warning-text) !important;
      font-weight: 700;
    }

    .form-row {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 16px;
    }

    .form-actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 24px;
      padding-top: 24px;
      border-top: 2px solid var(--ts-border-strong);
    }

    .btn-cancel {
      background: var(--ts-bg-elevated);
      color: var(--ts-text);
      border: 2px solid var(--ts-border-navy-650);
      padding: 12px 24px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.3s ease;
    }

    .btn-cancel:hover {
      border-color: var(--ts-border-blue-350-6);
      background: var(--ts-bg-navy-850-2);
    }

    .btn-save {
      background: var(--ts-bg);
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.3s ease;
    }

    .btn-save:hover:not(:disabled) {
      background: var(--ts-bg-gray-800);
    }

    .btn-save:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .clientes-directory { display: grid; gap: .7rem; }

    .status-badge {
      padding: 3px 9px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
    }

    .status-badge.active {
      background: var(--ts-bg-green-50);
      color: var(--ts-text-green-550);
    }

    .status-badge.inactive {
      background: var(--ts-bg-red-50);
      color: var(--ts-text-red-400);
    }

    /* El botón resalta el estado ACTUAL del cliente (verde = activo, rojo =
       inactivo), no la acción que ejecuta al presionarlo. */
    .status-toggle-btn.is-active { color: var(--ts-success); border-color: color-mix(in srgb, var(--ts-success) 40%, transparent); background: var(--ts-success-soft); }
    .status-toggle-btn.is-active:hover:not(:disabled) { background: color-mix(in srgb, var(--ts-success) 24%, transparent); color: var(--ts-text); }
    .status-toggle-btn.is-inactive { color: var(--ts-danger); border-color: color-mix(in srgb, var(--ts-danger) 40%, transparent); background: var(--ts-danger-soft); }
    .status-toggle-btn.is-inactive:hover:not(:disabled) { background: color-mix(in srgb, var(--ts-danger) 24%, transparent); color: var(--ts-text); }

    .header-tools {
      display: inline-flex;
      justify-self: end;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
    }

    .customer-search {
      width: min(100%, 430px);
      min-height: 42px;
      display: flex;
      align-items: center;
      gap: 9px;
      padding: 0 12px;
      color: var(--ts-text-blue-550-3);
      background: var(--ts-text);
      border: 1px solid var(--ts-border-sky-100-19);
      border-radius: 10px;
      box-shadow: 0 2px 10px var(--ts-shadow-navy-900-a5);
    }

    .customer-search:focus-within { border-color: var(--ts-border-blue-350-7); box-shadow: 0 0 0 3px var(--ts-shadow-blue-350-a14); }
    .customer-search input { flex: 1; min-width: 0; padding: 10px 0; border: 0; outline: 0; color: var(--ts-text-navy-850-3); background: transparent; font: inherit; }
    .customer-search input::placeholder { color: var(--ts-text-blue-350); }
    .customer-search button { display: grid; place-items: center; padding: 5px; border: 0; color: var(--ts-text-blue-550-3); background: transparent; cursor: pointer; }

    .customer-pagination {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 16px;
      margin-top: 18px;
      color: var(--ts-text-blue-550-3);
      font-size: 13px;
    }

    .customer-pagination button {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 8px 12px;
      border: 1px solid var(--ts-border-sky-100-19);
      border-radius: 8px;
      color: var(--ts-text-navy-750-6);
      background: var(--ts-text);
      cursor: pointer;
    }
    .customer-pagination button:disabled { opacity: .45; cursor: not-allowed; }

    @media (max-width: 1023px) {
      .usuarios-header {
        grid-template-columns: 1fr;
      }
      .search-submit {
        width: 100%;
      }
      .header-tools {
        order: -1;
        justify-self: stretch;
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
      }
      .page-size-field {
        width: 100%;
      }
      .page-size-field select {
        min-width: 0;
        width: 100%;
      }
    }
    @media (max-width: 760px) {
      .search-group { flex-direction: column; align-items: stretch; }
      .customer-search { width: 100%; box-sizing: border-box; }
      .search-group .customer-search { flex-basis: auto; }
      .header-tools { grid-template-columns: minmax(0, 1fr) auto; }
      .header-tools .btn-primary { white-space: nowrap; padding: 10px 14px; }
      .customer-pagination { flex-wrap: wrap; gap: 9px; }
      .customer-pagination span { width: 100%; text-align: center; order: -1; }
    }
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--ts-bg-gray-1000-a50);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 3000;
      padding: 20px;
    }

    .modal-content {
      color: var(--ts-text);
      background: var(--ts-bg-elevated);
      border: 1px solid var(--ts-border-strong);
      border-radius: 12px;
      padding: 32px;
      max-width: 500px;
      width: 100%;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 30px 80px var(--ts-shadow-gray-1000-a42);
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
    }

    .modal-header h3 {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
    }

  `]
})
export class UsuariosComponent implements OnInit {
  clienteForm!: FormGroup;
  editForm!: FormGroup;
  @ViewChild('clienteFormRef') clienteFormRef!: ElementRef<HTMLFormElement>;
  @ViewChild('editFormRef') editFormRef!: ElementRef<HTMLFormElement>;
  showCreateForm = signal<boolean>(false);
  editingCliente = signal<Cliente | null>(null);
  isCreating = signal<boolean>(false);
  isUpdating = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  processingCustomerId = signal<number | null>(null);
  // `searchDraft` es lo que el usuario está escribiendo; `searchTerm` es el
  // término realmente aplicado a la última búsqueda ejecutada (botón
  // "Buscar" o Enter) — ya no se busca en cada tecla.
  searchDraft = signal('');
  searchTerm = signal('');
  currentPage = signal(1);
  totalPages = signal(1);
  totalCustomers = signal(0);
  readonly pageSizeOptions = [25, 50, 75, 100];
  pageSize = signal(25);

  constructor(
    private fb: FormBuilder,
    public customerService: CustomerService,
    private authService: AuthService,
    private dialogs: DialogService,
    private notifications: NotificationService,
    private cooldown: ActionCooldownService,
  ) {}

  get businessId(): number | null {
    const business = this.authService.currentUser();
    return business?.id || null;
  }

  ngOnInit(): void {
    this.initForm();
    this.initEditForm();
    this.cargarClientes();
  }

  cargarClientes(): void {
    const businessId = this.businessId;
    if (businessId) {
      const loadingStartedAt = Date.now();
      this.isLoading.set(true);

      this.customerService.getClientesPaginados(businessId, this.currentPage(), this.pageSize(), this.searchTerm()).subscribe({
        next: response => this.aplicarRespuestaClientes(response, loadingStartedAt),
        error: (error) => {
          this.finishLoadingAfterMinimumDuration(loadingStartedAt);
          console.error('Error al cargar clientes:', error);
          // No mostrar error si es la primera carga, solo en consola
        }
      });
    }
  }

  onSearchInput(value: string): void {
    this.searchDraft.set(value.slice(0, 100));
  }

  buscarClientes(): void {
    if (!this.cooldown.consume('usuarios-search')) return;
    if (this.isLoading()) return;
    this.searchTerm.set(this.searchDraft().trim());
    this.currentPage.set(1);
    this.cargarClientes();
  }

  limpiarBusqueda(): void {
    if (this.isLoading()) return;
    this.searchDraft.set('');
    this.searchTerm.set('');
    this.currentPage.set(1);
    this.cargarClientes();
  }

  cambiarPagina(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.currentPage()) return;
    this.currentPage.set(page);
    this.cargarClientes();
  }

  onPageSizeChange(rawValue: number | string): void {
    const parsed = Number(rawValue);
    if (!this.pageSizeOptions.includes(parsed) || parsed === this.pageSize()) return;
    this.pageSize.set(parsed);
    this.currentPage.set(1);
    this.cargarClientes();
  }

  trackCliente(_index: number, cliente: Cliente): number {
    return cliente.user_id;
  }

  private aplicarRespuestaClientes(response: { customers: Cliente[]; total_customers: number }, loadingStartedAt: number): void {
    this.customerService.clientes.set(response.customers || []);
    this.totalCustomers.set(Number(response.total_customers || 0));
    this.totalPages.set(Math.max(1, Math.ceil(this.totalCustomers() / this.pageSize())));
    if (this.currentPage() > this.totalPages()) {
      this.currentPage.set(this.totalPages());
      this.cargarClientes();
      return;
    }
    this.finishLoadingAfterMinimumDuration(loadingStartedAt);
  }

  private finishLoadingAfterMinimumDuration(startedAt: number): void {
    finishLoadingAfter(startedAt, () => this.isLoading.set(false));
  }

  initForm(): void {
    this.clienteForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone_number: ['', Validators.required]
    });
  }

  initEditForm(): void {
    this.editForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone_number: ['', Validators.required]
    });
  }

  private getErrorMessage(error: any, fallback: string): string {
    return friendlyError(error, fallback);
  }

  closeForm(): void {
    this.showCreateForm.set(false);
    this.clienteForm.reset();
  }

  cancelarEdicion(): void {
    this.editingCliente.set(null);
    this.editForm.reset();
  }

  async crearCliente(): Promise<void> {
    if (!this.cooldown.consume('usuarios-create')) return;
    const businessId = this.businessId;
    if (!businessId) {
      this.notifications.error('No se pudo obtener el ID del negocio. Por favor, inicia sesión nuevamente.');
      return;
    }

    if (validateAndFocusFirstError(this.clienteForm, this.clienteFormRef?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los campos marcados antes de crear el cliente.')) {
      const formValue = this.clienteForm.value;
      const clienteRequest: CreateClienteRequest = {
        business_id: businessId,
        name: formValue.name,
        email: formValue.email,
        phone_number: formValue.phone_number
      };

      this.isCreating.set(true);
      try {
        const response = await this.dialogs.confirmAndRun(
          { title: 'Confirmar creación', message: '¿Creas este cliente y le envías un enlace por correo para que establezca su contraseña?', tone: 'info', confirmText: 'Crear cliente' },
          () => this.customerService.crearCliente(clienteRequest),
          'Cliente creado exitosamente',
          error => this.getErrorMessage(error, 'Error al crear el cliente'),
        );
        if (response !== null) {
          this.closeForm();

          // Garantizar visibilidad del cliente recién creado:
          // 1) Insertarlo de inmediato en la lista reactiva (sin esperar el round-trip)
          const nuevo = response?.customer;
          if (nuevo?.user_id) {
            const actuales = this.customerService.clientes();
            if (!actuales.some(c => c.user_id === nuevo.user_id)) {
              this.customerService.clientes.set([nuevo, ...actuales]);
            }
          }
          // 2) Volver a la primera página y limpiar la búsqueda para que el cliente
          //    nuevo (los más recientes van primero) siempre sea visible.
          this.searchDraft.set('');
          this.searchTerm.set('');
          this.currentPage.set(1);
          this.cargarClientes();
        }
      } catch (error) {
        console.error('Error al crear cliente:', error);
      } finally {
        this.isCreating.set(false);
      }
    } else {
      // Marcar todos los campos como touched para mostrar errores
      Object.keys(this.clienteForm.controls).forEach(key => {
        this.clienteForm.get(key)?.markAsTouched();
      });
    }
  }

  editarCliente(cliente: Cliente): void {
    this.editingCliente.set(cliente);
    this.editForm.patchValue({
      name: cliente.name,
      email: cliente.email,
      phone_number: cliente.phone_number
    });
  }

  async guardarEdicion(): Promise<void> {
    if (!this.cooldown.consume('usuarios-edit')) return;
    const cliente = this.editingCliente();
    const businessId = this.businessId;

    if (!cliente || !businessId) return;
    if (!validateAndFocusFirstError(this.editForm, this.editFormRef?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los campos marcados antes de guardar.')) return;

    this.isUpdating.set(true);
    const updateData: UpdateClienteRequest = this.editForm.value;

    try {
      const clienteActualizado = await this.dialogs.confirmAndRun(
        { title: 'Confirmar actualización', message: `¿Guardas los cambios de ${cliente.name}?`, tone: 'info', confirmText: 'Guardar cambios' },
        () => this.customerService.editarCliente(businessId, cliente.user_id, updateData),
        'Cliente actualizado exitosamente',
        error => this.getErrorMessage(error, 'Error al actualizar el cliente'),
      );
      if (clienteActualizado !== null) {
        this.cancelarEdicion();
        this.cargarClientes();
      }
    } catch (error) {
      console.error('Error al actualizar cliente:', error);
    } finally {
      this.isUpdating.set(false);
    }
  }

  async toggleEstadoCliente(cliente: Cliente): Promise<void> {
    const businessId = this.businessId;
    if (!businessId) {
      this.notifications.error('No se pudo obtener el ID del negocio');
      return;
    }

    const nuevoEstado = !cliente.is_active;
    const accion = nuevoEstado ? 'activar' : 'desactivar';

    this.processingCustomerId.set(cliente.user_id);
    try {
      const clienteActualizado = await this.dialogs.confirmAndRun(
        { title: `${nuevoEstado ? 'Activar' : 'Desactivar'} cliente`, message: `¿Deseas ${accion} a ${cliente.name}?`, confirmText: nuevoEstado ? 'Activar' : 'Desactivar', tone: nuevoEstado ? 'info' : 'danger' },
        () => this.customerService.actualizarEstadoCliente(businessId, cliente.user_id, nuevoEstado),
        `Cliente ${accion}do exitosamente`,
        (error: any) => this.getErrorMessage(error, `Error al ${accion} el cliente`),
      );
      if (clienteActualizado !== null) {
        this.customerService.clientes.update(clientes =>
          clientes.map(item => item.user_id === clienteActualizado.user_id ? clienteActualizado : item)
        );
      }
    } catch (error) {
      console.error(`Error al ${accion} cliente:`, error);
    } finally {
      this.processingCustomerId.set(null);
    }
  }

  async enviarRecuperacion(cliente: Cliente): Promise<void> {
    const businessId = this.businessId;
    if (!businessId) {
      this.notifications.error('No se pudo obtener el ID del negocio');
      return;
    }

    this.processingCustomerId.set(cliente.user_id);
    try {
      const response = await this.dialogs.confirmAndRun(
        { title: 'Enviar recuperación', message: `Se enviará a ${cliente.email} un enlace válido durante una hora. El negocio no podrá ver ni establecer la nueva contraseña.`, tone: 'warning', confirmText: 'Enviar enlace' },
        () => this.customerService.enviarRecuperacionContrasena(businessId, cliente.user_id),
        'Enlace de recuperación enviado.',
        (error: any) => this.getErrorMessage(error, 'No se pudo enviar el enlace de recuperación'),
      );
      void response;
    } catch (error) {
      console.error('Error al enviar recuperación:', error);
    } finally {
      this.processingCustomerId.set(null);
    }
  }

  async eliminarCliente(cliente: Cliente): Promise<void> {
    const businessId = this.businessId;
    if (!businessId) {
      this.notifications.error('No se pudo obtener el ID del negocio');
      return;
    }

    this.processingCustomerId.set(cliente.user_id);
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: 'Enviar cliente a la Papelera',
          message: `${cliente.name} dejará de aparecer en Clientes y podrá restaurarse durante 30 días.`,
          confirmText: 'Enviar a la Papelera',
          danger: true,
          fields: [{ name: 'reason', label: 'Motivo', type: 'textarea', required: true, placeholder: 'Explique por qué se retira este cliente' }],
        },
        values => this.customerService.eliminarCliente(businessId, cliente.user_id, String(values['reason']).trim()),
        'Cliente enviado a la Papelera exitosamente',
        (error: any) => this.getErrorMessage(error, 'Error al eliminar el cliente'),
      );
      if (result !== null) this.cargarClientes();
    } catch (error) {
      console.error('Error al eliminar cliente:', error);
    } finally {
      this.processingCustomerId.set(null);
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
}
